import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useSystemStatus } from '../contexts/SystemStatusContext';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from './Layout';

interface NavItem {
  href: string;
  label: string;
  icon: string;
  adminOnly?: boolean;
}

const ADMIN_EMAIL = 'tokmakoff@struvital.nl';

const navItems: NavItem[] = [
  { href: '/', label: 'Dashboard', icon: '📊' },
  { href: '/fleet-overview', label: 'Fleet Overview', icon: '🏭' },
  { href: '/attention', label: 'Need Attention', icon: '⚠️' },
  { href: '/tank-levels', label: 'Tank Levels', icon: '🛢️' },
  { href: '/tank-analytics', label: 'Tank Analytics', icon: '📈' },
  { href: '/cellars', label: 'Cellar Levels', icon: '🏗️' },
  { href: '/farm-map', label: 'Farm Map', icon: '🗺️' },
  { href: '/firmware', label: 'Firmware', icon: '⚡' },
  { href: '/remoteit', label: 'Remote.it Devices', icon: '🌐' },
  { href: '/experimental', label: 'Experimental', icon: '🧪' },
  { href: '/thingsboard-alerts', label: 'TB Alert Maintenance', icon: '🔧' },
  { href: '/device-review', label: 'Device Review', icon: '✅' },
  { href: '/settings', label: 'Settings', icon: '⚙️', adminOnly: true },
  { href: '/versions', label: 'System Versions', icon: '📦', adminOnly: true },
  { href: '/activity', label: 'Activity', icon: '📋', adminOnly: true },
  { href: '/ai-costs', label: 'AI Costs', icon: '💰', adminOnly: true },
];

export function SideNav() {
  const router = useRouter();
  const { collapsed, setCollapsed } = useSidebar();
  const { systemStatus: systemStatusData } = useSystemStatus();
  // Build a DataState-compatible wrapper so existing JSX (.data accessor) continues to work
  const systemStatus = { data: systemStatusData };
  const { user, logout } = useAuth();

  const isActive = (href: string) => {
    return router.pathname === href;
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
        onClick={() => setCollapsed(!collapsed)}
        aria-label={collapsed ? 'Open navigation menu' : 'Close navigation menu'}
        aria-expanded={!collapsed}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Overlay for mobile */}
      {!collapsed && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setCollapsed(true)}
        />
      )}

      {/* Side navigation */}
      <nav
        className={`flex flex-col flex-shrink-0 h-screen bg-white shadow-lg transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-64'
        } ${!collapsed ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} lg:translate-x-0 lg:relative lg:block fixed left-0 top-0 z-40`}
      >
        <div className="flex items-center justify-between p-4 border-b border-struvital-200">
          <div className={`flex items-center transition-opacity ${collapsed ? 'opacity-0' : 'opacity-100'}`}>
            <Image
              src="/struvital-logo.png"
              alt="Struvital"
              width={120}
              height={32}
              className="h-8 w-auto"
            />
          </div>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded hover:bg-struvital-100 text-struvital-700 hidden lg:block"
          >
            <svg
              className={`w-5 h-5 transition-transform ${collapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <ul className="py-4">
            {navItems
              .filter((item) => !item.adminOnly || user?.email === ADMIN_EMAIL)
              .map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className={`flex items-center px-4 py-3 hover:bg-struvital-50 transition-colors ${
                    isActive(item.href) ? 'bg-struvital-100 border-r-4 border-struvital-600 text-struvital-800' : 'text-gray-700'
                  }`}
                  title={collapsed ? item.label : undefined}
                >
                  <span className="text-xl">{item.icon}</span>
                  <span
                    className={`ml-3 font-medium transition-opacity ${
                      collapsed ? 'opacity-0 w-0' : 'opacity-100'
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {/* System Status - scrolls with nav items */}
          {!collapsed && (
            <div className="px-4 pb-4">
              <div className="bg-struvital-50 rounded-lg p-3 border border-struvital-200">
                <h3 className="text-xs font-semibold text-struvital-700 mb-2">System Status</h3>
                <div className="space-y-1">
                  {(() => {
                    const d = systemStatus.data;
                    const loading = !d;
                    const svc = d?.services;

                    type StatusInfo = { label: string; status: 'online' | 'offline' | 'disabled' | 'checking' | string };
                    const items: StatusInfo[] = [
                      { label: 'Backend', status: loading ? 'checking' : 'online' },
                      { label: 'TB Cloud', status: loading ? 'checking' : d?.thingsboard?.connected ? 'online' : 'offline' },
                      { label: 'TB-MCP', status: loading ? 'checking' : d?.mcp?.connected ? 'online' : 'offline' },
                      { label: 'Postgres', status: loading ? 'checking' : svc?.database?.connected ? 'online' : 'offline' },
                      { label: 'Redis', status: loading ? 'checking' : svc?.redis?.connected ? 'online' : 'offline' },
                      { label: 'AI', status: loading ? 'checking' : svc?.ai?.enabled ? (svc.ai.provider || 'active') : 'disabled' },
                      { label: 'n8n', status: loading ? 'checking' : !svc?.n8n?.enabled ? 'disabled' : svc.n8n.connected ? 'online' : 'offline' },
                      { label: 'Remote.it', status: loading ? 'checking' : !svc?.remoteit?.enabled ? 'disabled' : svc.remoteit.connected ? 'online' : 'offline' },
                      { label: 'Komodo', status: loading ? 'checking' : svc?.komodo?.connected ? 'online' : 'offline' },
                      { label: 'MongoDB', status: loading ? 'checking' : svc?.mongodb?.connected ? 'online' : 'offline' },
                    ];

                    const colorFor = (s: string) => {
                      if (s === 'checking') return 'text-gray-400 italic';
                      if (s === 'disabled') return 'text-gray-500';
                      if (s === 'online' || s === 'active') return 'text-green-700';
                      if (s === 'offline') return 'text-red-700';
                      return 'text-green-700';
                    };

                    return items.map(({ label, status }) => (
                      <div key={label} className="flex items-center justify-between">
                        <span className="text-xs text-struvital-800">{label}</span>
                        <span className={`text-xs font-medium ${colorFor(status)}`}>
                          {status === 'checking' ? 'checking...' : status}
                        </span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Pinned bottom: user + footer */}
        <div className="flex-shrink-0">
          {!collapsed && user && (
            <div className="px-4 pb-2">
              <div className="bg-struvital-50 rounded-lg p-3 border border-struvital-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-struvital-800 truncate">
                      {user.name || user.email}
                    </p>
                    <p className="text-xs text-struvital-800 truncate">
                      {user.authority}
                    </p>
                  </div>
                  <button
                    onClick={logout}
                    className="ml-2 p-1 rounded hover:bg-struvital-200 text-struvital-700"
                    title="Logout"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="p-4 border-t border-struvital-200">
            <div className={`text-xs text-struvital-800 ${collapsed ? 'hidden' : ''}`}>
              <p>Dashboard v{process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0'}</p>
              <p className="text-xs text-struvital-400">
                Build {process.env.NEXT_PUBLIC_BUILD_DATE?.split(' ')[0] || new Date().toISOString().split('T')[0]} | {process.env.NEXT_PUBLIC_GIT_BRANCH || 'main'} | git:{process.env.NEXT_PUBLIC_GIT_COMMIT || 'unknown'}
              </p>
              <p className="mt-1">© {new Date().getFullYear()} Struvital</p>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}